import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Route-level SEO defaults — guarantees every URL on the site ships with:
 *   1. A self-referencing <link rel="canonical"> pointing at the CURRENT
 *      production URL (not the preview host, not a homepage default).
 *   2. A <meta name="robots" content="index,follow,..."> tag so bots have
 *      an explicit indexing directive even if a page component forgets to
 *      call applySEO().
 *
 * Individual pages that call `applySEO()` will override the canonical target
 * (applySEO also selects the same <link rel="canonical"> element and updates
 * its href in place), so pages that know their preferred URL keep control.
 * This just kills the "no canonical at all" case that used to hit /news,
 * /author/:slug, /schedule, /about, /community, /film-room, and every legal
 * page — all of which previously shipped without a canonical after we
 * removed the hardcoded homepage canonical from index.html.
 */
const SITE_URL = "https://thetitanstandard.com";

// Query params that DO change the canonical (kept). Everything else is
// stripped so Google doesn't index dozens of ?utm= and ?ref= duplicates.
const KEEP_PARAMS = new Set(["tab", "id", "week", "year", "category"]);

function buildCanonical(pathname, search) {
  const params = new URLSearchParams(search || "");
  const keep = [];
  for (const [k, v] of params.entries()) {
    if (KEEP_PARAMS.has(k)) keep.push([k, v]);
  }
  const qs = keep.length ? "?" + keep.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&") : "";
  // Never emit a trailing slash except for the homepage — matches how
  // React Router normalises internal links.
  const cleanPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  return `${SITE_URL}${cleanPath}${qs}`;
}

function upsertCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
}

function upsertRobots() {
  let m = document.querySelector('meta[name="robots"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "robots");
    document.head.appendChild(m);
  }
  // Only reset the content if it's missing or someone stripped it — if a
  // page component set a stricter directive (e.g. noindex for admin), keep it.
  const current = (m.getAttribute("content") || "").toLowerCase();
  if (!current || current === "index,follow") {
    m.setAttribute("content", "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1");
  }
}

export default function SEODefaults() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // Never let admin routes get indexed — even if a page component forgets.
    if (pathname.startsWith("/admin")) {
      let m = document.querySelector('meta[name="robots"]');
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", "robots");
        document.head.appendChild(m);
      }
      m.setAttribute("content", "noindex,nofollow");
      // Admin surfaces don't get a canonical — they're private.
      const link = document.querySelector('link[rel="canonical"]');
      if (link) link.remove();
      return;
    }
    upsertCanonical(buildCanonical(pathname, search));
    upsertRobots();
  }, [pathname, search]);
  return null;
}
